<template>
  <div>
    <!-- Pilots who can be dragged into a slot. While a chip is being dragged out of a slot, dropping it here takes the pilot off that leg. -->
    <div
      v-if="props.canManagePilots && props.planePilots.length"
      class="border-b border-gray-200 px-4 py-3 transition-colors dark:border-gray-700 sm:px-6"
      :class="stripClasses"
      @dragover.prevent="
        crewDrag.isRemoveZone.value && crewDrag.allowMove($event)
      "
      @dragenter.prevent="
        crewDrag.isRemoveZone.value && crewDrag.enter('strip')
      "
      @dragleave="crewDrag.leave('strip')"
      @drop.prevent="crewDrag.dropOnRemoveZone()"
    >
      <ParagraphText size="text-xs" color="text-gray-500" class="mb-2">{{
        stripInstruction
      }}</ParagraphText>
      <div class="flex flex-wrap gap-2">
        <PilotChip
          v-for="pilot in props.planePilots"
          :key="pilot.id"
          :pilot="pilot"
          draggable
          @dragstart="crewDrag.startDrag(pilot, null, $event)"
          @dragend="crewDrag.endDrag()"
        />
      </div>
    </div>

    <!-- Column headings, only where there's room for columns -->
    <div
      class="hidden border-b border-gray-300 bg-gray-50 px-6 py-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 sm:grid"
      :class="columns"
    >
      <div class="sm:col-span-1">Leg</div>
      <div class="sm:col-span-2">PIC</div>
      <div class="sm:col-span-2">SIC</div>
    </div>

    <!-- One block per leg: stacked on phones, a row of three columns from the sm breakpoint up -->
    <div class="divide-y divide-gray-200 dark:divide-gray-600">
      <div
        v-for="leg in props.legs"
        :key="leg.id"
        class="grid grid-cols-1 gap-y-2 px-4 py-3 sm:gap-x-4 sm:px-6"
        :class="columns"
      >
        <div
          class="flex flex-wrap items-baseline gap-x-2 sm:col-span-1 sm:block"
        >
          <ParagraphText size="text-sm" font="font-medium">{{
            legRoute(leg)
          }}</ParagraphText>
          <ParagraphText size="text-xs" color="text-gray-500">{{
            legShortDate(leg)
          }}</ParagraphText>
          <ParagraphText
            v-if="props.canManagePilots && props.legs.length > 1"
            size="text-xs"
            class="ml-auto sm:ml-0 sm:mt-1"
          >
            <LinkBase size="xs" @click.prevent="emit('copy', leg)"
              >Use for all legs</LinkBase
            >
          </ParagraphText>
        </div>

        <div
          v-for="role in roles"
          :key="role"
          class="-mx-1 flex items-start gap-2 rounded-md px-1 py-0.5 transition-colors sm:col-span-2"
          :class="slotClasses(leg, role)"
          @dragover.prevent="
            crewDrag.slotAccepts(leg, role) && crewDrag.allowMove($event)
          "
          @dragenter.prevent="
            crewDrag.slotAccepts(leg, role) &&
              crewDrag.enter(slotKey(leg, role))
          "
          @dragleave="crewDrag.leave(slotKey(leg, role))"
          @drop.prevent="crewDrag.dropOnSlot(leg, role)"
        >
          <SpanText
            class="w-7 flex-shrink-0 pt-1 text-xs font-medium uppercase sm:hidden"
            color="text-gray-500"
            >{{ role }}</SpanText
          >
          <div class="flex min-w-0 flex-wrap items-center gap-1.5">
            <PilotChip
              v-for="pilot in crewIn(leg, role)"
              :key="pilot.id"
              :pilot="pilot"
              :draggable="props.canManagePilots"
              :removable="props.canManagePilots"
              :removeTitle="`Take ${pilot.name} off this leg`"
              @dragstart="crewDrag.startDrag(pilot, leg, $event)"
              @dragend="crewDrag.endDrag()"
              @remove="crewDrag.unassign(leg, pilot)"
            />
            <LinkBase
              v-if="props.canManagePilots && !crewIn(leg, role).length"
              size="xs"
              class="py-1"
              :color="crewDrag.drag.value ? 'text-indigo-600' : 'text-red-600'"
              @click.prevent="emit('assign', leg, role)"
              >{{
                crewDrag.drag.value ? 'Drop here' : `Assign ${roleLabel(role)}`
              }}</LinkBase
            >
            <SpanText
              v-else-if="!crewIn(leg, role).length"
              size="text-xs"
              color="text-gray-400"
              class="py-1"
              >None</SpanText
            >
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="props.trip.pilots.length"
      class="space-y-1 border-t border-gray-200 px-4 py-3 dark:border-gray-700 sm:px-6"
    >
      <div
        v-for="pilot in props.trip.pilots"
        :key="pilot.id"
        class="flex items-start justify-between gap-4"
      >
        <ParagraphText size="text-sm" class="min-w-0">
          <SpanText font="font-medium">{{ pilot.name }}</SpanText>
          <SpanText color="text-gray-500">
            · {{ pilotSummary(pilot, props.legs) }}</SpanText
          >
        </ParagraphText>
        <LinkBase
          v-if="props.canManagePilots"
          size="xs"
          class="flex-shrink-0 whitespace-nowrap"
          @click.prevent="emit('remove', pilot)"
          >Remove from trip</LinkBase
        >
      </div>
    </div>
  </div>
</template>

<script setup>
import ParagraphText from '@/Common/Text/ParagraphText.vue'
import SpanText from '@/Common/Text/SpanText.vue'
import LinkBase from '@/Common/Text/LinkBase.vue'
import PilotChip from './PilotChip.vue'
import { useCrewDrag } from './useCrewDrag'
import { computed } from 'vue'
import {
  legRoute,
  legShortDate,
  pilotSummary,
  roleLabel,
  sortCrew,
} from '../Calendar/pilotColors'

const props = defineProps({
  trip: Object,
  legs: Array,
  planePilots: Array,
  canManagePilots: Boolean,
})

// assign(leg, role): open the picker for an empty slot
// remove(pilot): take the pilot off the whole trip
// copy(leg): give every leg this leg's crew
const emit = defineEmits(['assign', 'remove', 'copy'])

const roles = ['pic', 'sic']

// Leg, PIC, SIC once there's room for columns: one fifth for the leg, two each for the slots
const columns = 'sm:grid-cols-5'

function crewIn(leg, role) {
  return sortCrew(leg.pilots).filter((pilot) => pilot.pivot?.role === role)
}

// Drag and drop

const crewDrag = useCrewDrag()

function slotKey(leg, role) {
  return `${leg.id}-${role}`
}

function slotClasses(leg, role) {
  if (!crewDrag.drag.value) return ''

  if (crewDrag.isOver(slotKey(leg, role)))
    return 'bg-indigo-50 ring-2 ring-inset ring-indigo-400 dark:bg-indigo-900/40'

  return crewDrag.slotAccepts(leg, role) ? 'bg-gray-50 dark:bg-gray-700/40' : ''
}

const stripInstruction = computed(() => {
  if (crewDrag.isRemoveZone.value)
    return `Drop here to take ${crewDrag.drag.value.pilot.name} off that leg`

  if (crewDrag.drag.value) return 'Drop on a PIC or SIC slot below'

  return 'Drag a pilot onto a PIC or SIC slot, or tap an empty slot to choose one.'
})

const stripClasses = computed(() => {
  if (!crewDrag.isRemoveZone.value) return ''

  return crewDrag.isOver('strip')
    ? 'bg-red-50 ring-2 ring-inset ring-red-400 dark:bg-red-900/40'
    : 'bg-red-50/50 dark:bg-red-900/20'
})
</script>
